import { useState, useEffect } from "react";
import { Search, ChevronUp, AlertTriangle, Info, Users, ClipboardCheck, Utensils, MessageSquare, FileText, Bell, HelpCircle, Shield, BarChart3, Download, Calendar, Waves, Bus, Flag, Trophy, MapPin, ToggleLeft, Trash2, Printer, ExternalLink, CalendarDays, Eye, ArrowLeftRight, Swords } from "lucide-react";
import SopSwitcher from "./SopSwitcher";
import "../styles/tutorialPrint.css";

const API = process.env.REACT_APP_BACKEND_URL;
const IMG = {
  login: `${API}/api/assets/tutorial/01_login.png`,
  homescreen: `${API}/api/assets/tutorial/02_homescreen.png`,
  attendance: `${API}/api/assets/tutorial/03_attendance.png`,
  buddy: `${API}/api/assets/tutorial/04_buddy.png`,
  messages: `${API}/api/assets/tutorial/05_messages.png`,
  incidents: `${API}/api/assets/tutorial/06_incidents.png`,
  alerts: `${API}/api/assets/tutorial/07_alerts.png`,
  roster: `${API}/api/assets/tutorial/08_roster.png`,
  campers: `${API}/api/assets/tutorial/09_campers.png`,
};

const SECTIONS = [
  { id: "who-is-supervisor", num: "1", title: "Who is a Supervisor — Your Job in Plain English" },
  { id: "how-to-login", num: "2", title: "How to Log In — Step by Step" },
  { id: "your-dashboard", num: "3", title: "What You See When You Log In — Your Dashboard" },
  { id: "finding-campers", num: "4", title: "Campers — How to Find Any Camper in Your Division" },
  { id: "daily-roster", num: "5", title: "Daily Roster — Who Is Here Today (You Can ONLY Look, Not Edit)" },
  { id: "attendance-conflicts", num: "6", title: "Attendance Conflicts — Your #1 Morning Job" },
  { id: "buddy-sheets", num: "7", title: "Buddy Sheets — Pool Safety Check" },
  { id: "incident-reports", num: "8", title: "Incident Reports — When Something Happens" },
  { id: "trip-forms", num: "9", title: "Trip Forms — Before Any Bus Leaves Camp" },
  { id: "messages", num: "10", title: "Messages — How to Contact Parents" },
  { id: "group-schedule", num: "11", title: "Group Schedules — Regular / Special Event / Rainy Day" },
  { id: "hobbies-intensives", num: "12", title: "Hobbies & Intensives Reports" },
  { id: "mariner-intramurals", num: "13", title: "Mariner Intramurals (Mariners Supervisor ONLY)" },
  { id: "nav-intramurals", num: "14", title: "Navigator Intramurals (Navigators Supervisor ONLY)" },
  { id: "attendance-portal", num: "15", title: "The Attendance Admin Portal (External Website)" },
  { id: "dining-portal", num: "16", title: "The Dining Admin Portal (External Website)" },
  { id: "aquatics", num: "17", title: "Aquatics — Swim Track (External Website)" },
  { id: "cit-program", num: "18", title: "CIT Program — Counselors in Training" },
  { id: "athletics-dashboard", num: "19", title: "Athletics Supervisor Dashboard" },
  { id: "color-war", num: "20", title: "Color War — The Reveal Switch" },
  { id: "testing-tools", num: "21", title: "Testing Tools — DO NOT TOUCH During Summer" },
  { id: "escalation", num: "22", title: "When Things Go Wrong — Who to Call" },
];

function Why({ children }) {
  return (
    <div className="sop-why sop-block bg-emerald-50 border-l-4 border-emerald-600 p-4 rounded-r-lg my-4">
      <p className="text-sm font-bold text-emerald-900 mb-1 flex items-center gap-1"><Info className="w-4 h-4" /> Why does this matter?</p>
      <p className="text-sm text-emerald-800">{children}</p>
    </div>
  );
}

function Mistakes({ items }) {
  return (
    <div className="sop-mistakes sop-block bg-amber-50 border-l-4 border-amber-500 p-4 rounded-r-lg my-4">
      <p className="text-sm font-bold text-amber-900 mb-2 flex items-center gap-1"><AlertTriangle className="w-4 h-4" /> DO NOT do these things</p>
      <ul className="list-disc pl-5 space-y-1">
        {items.map((item, i) => <li key={i} className="text-sm text-amber-800">{item}</li>)}
      </ul>
    </div>
  );
}

function Img({ caption, src }) {
  if (!src) return null;
  return (
    <div className="sop-img sop-block my-4 rounded-xl overflow-hidden border shadow-sm">
      <img src={src} alt={caption} className="w-full object-contain max-h-[500px] bg-gray-50" loading="lazy" />
      <p className="text-xs text-gray-500 italic px-3 py-2 bg-gray-50 border-t">{caption}</p>
    </div>
  );
}

function Steps({ items }) {
  return (
    <ol className="sop-steps sop-block list-decimal pl-6 space-y-2 my-3">
      {items.map((item, i) => <li key={i} className="text-base text-gray-700 leading-relaxed">{item}</li>)}
    </ol>
  );
}

function H({ id, num, title, icon: Icon }) {
  return (
    <div id={id} className="sop-section scroll-mt-20 pt-10 pb-3 border-b-2 border-gray-200">
      <div className="flex items-center gap-3">
        {Icon && <Icon className="w-6 h-6 text-emerald-700 flex-shrink-0" />}
        <h2 className="text-xl sm:text-2xl font-bold text-gray-900">{num}. {title}</h2>
      </div>
    </div>
  );
}

function Rule({ children }) {
  return (
    <div className="sop-block p-4 bg-red-50 border-2 border-red-500 rounded-xl my-4">
      <p className="font-bold text-red-900">{children}</p>
    </div>
  );
}

function Callout({ title, children, color = "sky" }) {
  const colors = {
    sky: "bg-sky-50 border-sky-300 text-sky-900",
    indigo: "bg-indigo-50 border-indigo-300 text-indigo-900",
    amber: "bg-amber-50 border-amber-300 text-amber-900",
    rose: "bg-rose-50 border-rose-300 text-rose-900",
    emerald: "bg-emerald-50 border-emerald-300 text-emerald-900",
  };
  return (
    <div className={`sop-block p-4 rounded-xl border-2 my-4 ${colors[color]}`}>
      {title && <p className="font-bold text-sm mb-2">{title}</p>}
      <div className="text-sm">{children}</div>
    </div>
  );
}

export default function TutorialSupervisors() {
  const [search, setSearch] = useState("");
  const [showBackToTop, setShowBackToTop] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const h = () => setShowBackToTop(window.scrollY > 400);
    window.addEventListener("scroll", h);
    return () => window.removeEventListener("scroll", h);
  }, []);

  const filtered = search ? SECTIONS.filter(s => s.title.toLowerCase().includes(search.toLowerCase())) : SECTIONS;

  return (
    <div className="min-h-screen bg-white" data-testid="tutorial-supervisor-page">
      <header className="sticky top-0 z-50 bg-emerald-900 text-white shadow-lg print:static print:bg-white print:text-black">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between gap-2">
          <div className="flex items-center gap-3">
            <div>
              <h1 className="text-lg sm:text-xl font-bold">Rolling River Day Camp</h1>
              <p className="text-xs text-emerald-200">Supervisor Tutorial</p>
            </div>
            <div className="hidden sm:block">
              <SopSwitcher current="/tutorialsupervisors" accent="emerald" />
            </div>
          </div>
          <div className="flex items-center gap-2 print:hidden">
            <div className="sm:hidden">
              <SopSwitcher current="/tutorialsupervisors" accent="emerald" />
            </div>
            <a href={`${API}/api/tutorial/pdf/supervisors`} className="text-xs bg-white text-emerald-900 hover:bg-emerald-50 px-3 py-1.5 rounded-lg font-semibold inline-flex items-center gap-1" data-testid="download-pdf-btn">
              <Download className="w-3.5 h-3.5" /> Download PDF
            </a>
            <button onClick={() => window.print()} className="text-xs bg-emerald-700 hover:bg-emerald-600 px-3 py-1.5 rounded-lg font-medium inline-flex items-center gap-1" data-testid="print-btn">
              <Printer className="w-3.5 h-3.5" /> Print
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-4 py-6 lg:flex lg:gap-8">
        {/* TOC */}
        <nav aria-label="TOC" className="lg:w-64 lg:flex-shrink-0 mb-6 lg:mb-0 print:hidden">
          <button onClick={() => setMenuOpen(!menuOpen)} className="lg:hidden w-full text-left bg-gray-50 border rounded-xl p-3 text-sm font-bold text-gray-700 flex items-center justify-between" data-testid="toc-toggle">
            Table of Contents <ChevronUp className={`w-4 h-4 transition-transform ${menuOpen ? "" : "rotate-180"}`} />
          </button>
          <div className={`lg:sticky lg:top-20 ${menuOpen ? "block" : "hidden lg:block"} mt-2 lg:mt-0`}>
            <div className="relative mb-3">
              <Search className="w-4 h-4 absolute left-3 top-2.5 text-gray-400" />
              <input type="text" placeholder="Search sections..." value={search} onChange={e => setSearch(e.target.value)} className="w-full pl-9 pr-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-400" data-testid="toc-search-input" />
            </div>
            <ul className="space-y-0.5 max-h-[70vh] overflow-y-auto">
              {filtered.map(s => (
                <li key={s.id}><a href={`#${s.id}`} onClick={() => setMenuOpen(false)} className="block px-3 py-1.5 text-sm rounded-lg hover:bg-emerald-50 text-gray-600" data-testid={`toc-link-${s.id}`}>{s.num}. {s.title}</a></li>
              ))}
            </ul>
          </div>
        </nav>

        {/* Content */}
        <main className="flex-1 min-w-0">
          {/* INTRO BANNER */}
          <section className="mb-8">
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-3">The Supervisor Role</h2>
            <p className="text-base text-gray-700 leading-relaxed mb-4">
              You are <strong>not</strong> a counselor. You do <strong>not</strong> run a group. You <strong>oversee an entire division</strong> of groups. Counselors do the daily work. You watch them, fix problems they cannot fix, and make sure nothing falls through the cracks.
            </p>
            <p className="text-base text-gray-700 leading-relaxed mb-4">
              This guide covers <strong>every single thing</strong> your dashboard can do. If a feature exists on your screen, it is in this document. Read it top to bottom at least once. Then keep it bookmarked.
            </p>
            <p className="text-base text-gray-700 leading-relaxed mb-4">
              Nothing in this tutorial is the same as the Counselor Tutorial. Counselors have their own guide. If you need to understand what a counselor sees, read the <a href="/tutorialcounselor" className="text-emerald-700 underline font-semibold">Counselor Tutorial</a> separately.
            </p>

            <div className="bg-emerald-50 border-2 border-emerald-400 rounded-xl p-5 mb-5">
              <h3 className="text-lg font-bold text-emerald-900 mb-2">There are FOUR divisions. Three of them are run by a supervisor — each supervisor owns ONE division.</h3>
              <ul className="list-disc pl-5 space-y-2 text-sm text-emerald-800">
                <li><strong>Mariners Supervisor</strong> — You oversee groups <strong>B01 through B11</strong> (boys) and <strong>G01 through G11</strong> (girls). 1st through 4th grade.</li>
                <li><strong>Skippers Supervisor</strong> — You oversee groups <strong>N01 through N16</strong>. Nursery through Kindergarten.</li>
                <li><strong>Navigators Supervisor</strong> — You oversee groups <strong>B12 through B15</strong> (boys) and <strong>G12 through G15</strong> (girls). 5th through 8th grade.</li>
                <li><strong>CIT Division</strong> — Group <strong>CIT</strong> (Counselors in Training, ages 15–16). Run by <strong>2 CIT Supervisors</strong>, not by one of the three division supervisors above. CIT has its own paperwork, trip, and incident workflow — see <a href="#cit-program" className="underline font-semibold">Section 18: CIT Program</a>.</li>
              </ul>
              <p className="text-sm text-emerald-700 font-bold mt-3">You can ONLY see your own division. You cannot see or touch another division's data. Period.</p>
            </div>

            <div className="bg-red-50 border-2 border-red-400 rounded-xl p-5 mb-5">
              <h3 className="text-lg font-bold text-red-900 mb-2">Your Non-Negotiable Responsibilities Every Single Day</h3>
              <ol className="list-decimal pl-5 space-y-2 text-sm text-red-800">
                <li><strong>Resolve every red attendance conflict by 9:35 AM.</strong> Counselors CANNOT do this. Only you can.</li>
                <li><strong>Respond to every incident report within 30 minutes.</strong> Read it, document your follow-up, escalate if needed.</li>
                <li><strong>Navigators Supervisor:</strong> Enter Navigator Intramural game scores the same day a match is played.</li>
                <li><strong>Athletics Supervisor:</strong> Enter Mariner Intramural game scores (boys and girls) the same day a match is played.</li>
                <li><strong>Aquatics Supervisor:</strong> Check every group's Buddy Sheet at the pool deck before they enter the water, and at every buddy whistle. Stop the swim if anything is wrong.</li>
              </ol>
            </div>

            <Callout title="Tech Support" color="sky">
              <p>Something broken? Contact <strong>Brian Stein</strong>. Do not try to fix bugs yourself. Report it and move on with your day.</p>
            </Callout>

            <p className="text-xs text-gray-400">Last updated: February 2026 (SOP refresh: AM/PM exceptions, late/dismissed bus rules, buddy sheet admin lock, CIT private notes, off-season message gate)</p>
          </section>

          {/* ═══ 1. WHO IS A SUPERVISOR ═══ */}
          <H id="who-is-supervisor" num="1" title="Who is a Supervisor — Your Job in Plain English" icon={Shield} />
          <div className="mt-4 space-y-3">
            <p className="text-base text-gray-700">You have exactly four jobs. Nothing more, nothing less:</p>
            <div className="space-y-2">
              <div className="p-4 bg-gray-50 rounded-xl border">
                <p className="font-bold text-gray-800">Job 1: Enforce deadlines.</p>
                <p className="text-sm text-gray-600 mt-1">Every counselor in your division must submit bus attendance by <strong>9:30 AM</strong>, group attendance by <strong>10:00 AM</strong>, and lunch orders by <strong>9:30 AM</strong>. If someone hasn't submitted, you track them down <strong>in person</strong> — counselors don't carry radios (only supervisors, pool, and boating do).</p>
              </div>
              <div className="p-4 bg-gray-50 rounded-xl border">
                <p className="font-bold text-gray-800">Job 2: Resolve conflicts and problems.</p>
                <p className="text-sm text-gray-600 mt-1">Red attendance conflicts, parent complaints, camper behavior issues, group reassignments. Counselors flag these. You fix them.</p>
              </div>
              <div className="p-4 bg-gray-50 rounded-xl border">
                <p className="font-bold text-gray-800">Job 3: Run reports and monitor programs.</p>
                <p className="text-sm text-gray-600 mt-1">Hobbies selections, Intensives rosters, Intramural scores and standings. You make sure the data is right and the programs are running on track.</p>
              </div>
              <div className="p-4 bg-gray-50 rounded-xl border">
                <p className="font-bold text-gray-800">Job 4: Be the link between counselors and camp leadership.</p>
                <p className="text-sm text-gray-600 mt-1">Counselors come to you first. You go to the camp director. If a counselor calls the director directly, that is your failure.</p>
              </div>
            </div>
          </div>

          {/* ═══ 2. HOW TO LOG IN ═══ */}
          <H id="how-to-login" num="2" title="How to Log In" icon={Shield} />
          <div className="mt-4 space-y-3">
            <Rule>Supervisors do NOT log in with a group code. You use your division name.</Rule>

            <Steps items={[
              <span key="s1">Open <strong>campersnapshot.com</strong>.</span>,
              <span key="s2">Tap the <strong>Supervisor</strong> tab.</span>,
              <span key="s3"><strong>Username</strong> = your division name (all lowercase): <code className="bg-gray-100 px-1 rounded text-sm">mariners</code>, <code className="bg-gray-100 px-1 rounded text-sm">skippers</code>, or <code className="bg-gray-100 px-1 rounded text-sm">navigators</code>.</span>,
              <span key="s4"><strong>Password</strong> = the supervisor password (ask the camp director if you don't have it).</span>,
              <span key="s5">Tap <strong>Log In</strong>. You land on your division dashboard, scoped to your groups only.</span>,
            ]} />
          </div>

          {/* ═══ 3. YOUR DASHBOARD ═══ */}
          <H id="your-dashboard" num="3" title="What You See When You Log In — Your Dashboard" icon={BarChart3} />
          <div className="mt-4 space-y-3">
            <p className="text-base text-gray-700">After you log in, you see a grid of cards. Each card is a feature. Tap a card to open that feature. Tap the <strong>Dashboard</strong> button in the top-left to go back to this grid at any time.</p>

            <p className="text-base text-gray-700 font-bold mt-4">Cards that EVERY supervisor sees (Mariners, Skippers, and Navigators):</p>
            <div className="space-y-2">
              <div className="p-3 bg-white border rounded-lg flex items-start gap-3"><Users className="w-5 h-5 text-indigo-600 mt-0.5 flex-shrink-0" /><div><p className="font-bold text-sm">Campers</p><p className="text-xs text-gray-500">Every camper in your division. Search by name or tap a group to see its roster.</p></div></div>
              <div className="p-3 bg-white border rounded-lg flex items-start gap-3"><CalendarDays className="w-5 h-5 text-cyan-600 mt-0.5 flex-shrink-0" /><div><p className="font-bold text-sm">Daily Roster</p><p className="text-xs text-gray-500">Read-only list of who is at camp today, group by group. You cannot edit anything here.</p></div></div>
              <div className="p-3 bg-white border rounded-lg flex items-start gap-3"><ClipboardCheck className="w-5 h-5 text-emerald-600 mt-0.5 flex-shrink-0" /><div><p className="font-bold text-sm">Buddy Sheets</p><p className="text-xs text-gray-500">AM and PM pool buddy sheets your counselors filed. You review them for safety.</p></div></div>
              <div className="p-3 bg-white border rounded-lg flex items-start gap-3"><AlertTriangle className="w-5 h-5 text-rose-600 mt-0.5 flex-shrink-0" /><div><p className="font-bold text-sm">Incident Reports</p><p className="text-xs text-gray-500">Injury, behavior, or conflict reports filed by counselors. Badge shows how many are open.</p></div></div>
              <div className="p-3 bg-white border rounded-lg flex items-start gap-3"><Bus className="w-5 h-5 text-teal-600 mt-0.5 flex-shrink-0" /><div><p className="font-bold text-sm">Trip Forms</p><p className="text-xs text-gray-500">Trip accountability forms. Check headcount before and after every trip.</p></div></div>
              <div className="p-3 bg-white border rounded-lg flex items-start gap-3"><MessageSquare className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" /><div><p className="font-bold text-sm">Messages</p><p className="text-xs text-gray-500">Send emails and/or SMS to one camper's family, one group, or your whole division.</p></div></div>
              <div className="p-3 bg-white border rounded-lg flex items-start gap-3"><Flag className="w-5 h-5 text-fuchsia-600 mt-0.5 flex-shrink-0" /><div><p className="font-bold text-sm">Color War</p><p className="text-xs text-gray-500">See team assignments. The reveal toggle is controlled by admin.</p></div></div>
              <div className="p-3 bg-white border rounded-lg flex items-start gap-3"><Trash2 className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" /><div><p className="font-bold text-sm">Testing Tools</p><p className="text-xs text-gray-500">Destructive. Wipes data. Pre-season only. See Section 17.</p></div></div>
            </div>

            <p className="text-base text-gray-700 font-bold mt-4">Cards that appear ONLY for certain divisions:</p>
            <div className="overflow-x-auto">
              <table className="w-full text-sm border-collapse my-2">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="border p-2 text-left">Card</th>
                    <th className="border p-2 text-center">Mariners</th>
                    <th className="border p-2 text-center">Skippers</th>
                    <th className="border p-2 text-center">Navigators</th>
                  </tr>
                </thead>
                <tbody>
                  <tr><td className="border p-2 font-medium">Hobbies Reports</td><td className="border p-2 text-center font-bold text-emerald-700">YES</td><td className="border p-2 text-center text-gray-400">No</td><td className="border p-2 text-center font-bold text-emerald-700">YES</td></tr>
                  <tr><td className="border p-2 font-medium">Intensives Reports</td><td className="border p-2 text-center text-gray-400">No</td><td className="border p-2 text-center text-gray-400">No</td><td className="border p-2 text-center font-bold text-emerald-700">YES</td></tr>
                  <tr><td className="border p-2 font-medium">Intramurals</td><td className="border p-2 text-center font-bold text-emerald-700">YES</td><td className="border p-2 text-center text-gray-400">No</td><td className="border p-2 text-center font-bold text-emerald-700">YES</td></tr>
                </tbody>
              </table>
            </div>

            <p className="text-base text-gray-700 font-bold mt-4">External links (these open a NEW browser tab — they are separate websites):</p>
            <div className="space-y-2">
              <div className="p-3 bg-violet-50 border border-violet-200 rounded-lg flex items-start gap-3"><ExternalLink className="w-5 h-5 text-violet-600 mt-0.5 flex-shrink-0" /><div><p className="font-bold text-sm">Attendance — rrdcattendance.com</p><p className="text-xs text-gray-500">The master attendance system. Where you correct attendance mistakes. See Section 14.</p></div></div>
              <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg flex items-start gap-3"><ExternalLink className="w-5 h-5 text-yellow-600 mt-0.5 flex-shrink-0" /><div><p className="font-bold text-sm">Dining — rrdcdining.com/admin</p><p className="text-xs text-gray-500">The master lunch system. Where you add or fix lunch orders. See Section 15.</p></div></div>
            </div>

            <Img caption="Your dashboard after login. All your cards and external links are here. Division-specific cards show automatically." src={IMG.homescreen} />
          </div>

          {/* ═══ 4. CAMPERS ═══ */}
          <H id="finding-campers" num="4" title="Campers — How to Find Any Camper in Your Division" icon={Users} />
          <div className="mt-4 space-y-3">
            <p className="text-base text-gray-700">This is where you go when a parent calls, a counselor messages or finds you in person with a question, or the front desk needs info about a camper. You need to be able to find any camper in under 15 seconds.</p>

            <p className="text-base text-gray-700 font-bold mt-3">How to open it:</p>
            <Steps items={[
              "From your dashboard, tap the Campers card.",
              "You see a grid of group cards. Each card shows the group code (like B03 or G07) and how many campers are in it.",
            ]} />

            <p className="text-base text-gray-700 font-bold mt-3">To find a camper by name (fastest way):</p>
            <Steps items={[
              "At the top of the Campers screen, there is a search bar that says \"Search any camper by name...\"",
              "Start typing the camper's name. Results appear instantly.",
              "Tap the camper's name. It opens that group's full roster.",
            ]} />

            <p className="text-base text-gray-700 font-bold mt-3">To browse a specific group:</p>
            <Steps items={[
              "Tap any group card (like B05 or G09).",
              "You see every camper in that group as a card.",
              "Tap a camper card to open the full Camper Detail.",
            ]} />

            <p className="text-base text-gray-700 font-bold mt-3">What is on a Camper Detail card:</p>
            <ul className="list-disc pl-6 space-y-1 text-sm text-gray-700">
              <li><strong>Medical info</strong> — allergies, medications, conditions</li>
              <li><strong>Parent contacts</strong> — phone numbers, email addresses</li>
              <li><strong>Swim color</strong> — what depth pool level they are cleared for</li>
              <li><strong>Bus info</strong> — bus stop, route</li>
              <li><strong>Authorized pickup list</strong> — who is allowed to pick them up</li>
              <li><strong>Session type</strong> — Full Season, Half Season, Flex days, etc.</li>
              <li><strong>Download PDF button</strong> — downloads a printable PDF of all their info</li>
            </ul>

            <Img caption="Campers view. Group cards on the left, search bar at top." src={IMG.campers} />
          </div>

          {/* ═══ 5. DAILY ROSTER ═══ */}
          <H id="daily-roster" num="5" title="Daily Roster — Read-Only" icon={CalendarDays} />
          <div className="mt-4 space-y-3">
            <Rule>The Daily Roster is read-only for supervisors. To change attendance, lunch, or swim status, use the external portals (Sections 14 and 15).</Rule>

            <Steps items={[
              "From your dashboard, tap the Daily Roster card.",
              "Tap a group tile to see every camper at camp today in that group.",
              "Each camper shows name, age, group code, and session type.",
              "Use the date picker (top-right) to switch days.",
              "Tap a camper to open their full Camper Detail card.",
            ]} />
            <Img caption="Daily Roster — tap a group, see who is here. View-only." src={IMG.roster} />
          </div>

          {/* ═══ 6. ATTENDANCE CONFLICTS ═══ */}
          <H id="attendance-conflicts" num="6" title="Attendance Conflicts — Your #1 Morning Job" icon={ClipboardCheck} />
          <div className="mt-4 space-y-3">
            <Rule>ONLY supervisors can resolve attendance conflicts. A conflict = the Attendance App and Bus App disagree about a camper.</Rule>

            <Steps items={[
              <span key="c1">At <strong>9:25 AM</strong>, look for groups with a red conflict badge.</span>,
              "Tap the group → Conflict Match opens.",
              <span key="c4">Decide which source is correct. If unsure, find the counselor or call the parent. Do NOT guess.</span>,
              <span key="c5">Tap <strong>Accept App</strong> or <strong>Accept Bus</strong>. Row locks for the day.</span>,
              <span key="c6">Late arrivals auto-resolve when front desk marks Late. Leave amber rows alone.</span>,
              <span key="c7">By <strong>9:35 AM</strong>, zero red rows. Escalate any holdouts to the camp director.</span>,
            ]} />
            <div className="p-3 bg-emerald-50 border-l-4 border-emerald-500 rounded-r-lg">
              <p className="text-sm text-emerald-900"><strong>Auto-SMS:</strong> The system <strong>automatically texts you</strong> the moment a counselor's group attendance produces a red conflict. You do not need to wait for the counselor to come find you — open Conflict Match as soon as the text hits.</p>
            </div>
            <Img caption="Conflict Match — red rows are conflicts. YOU tap Accept App or Accept Bus." src={IMG.attendance} />
          </div>

          {/* ═══ 7. BUDDY SHEETS ═══ */}
          <H id="buddy-sheets" num="7" title="Buddy Sheets — Pool Safety Check" icon={Waves} />
          <div className="mt-4 space-y-3">
            <Rule>Counselors build buddy sheets (AM and PM separately, same swim color pairs). You spot-check.</Rule>

            <Steps items={[
              "Tap the Buddy Sheets card. Sheets grouped by date, newest first.",
              <span key="bs4">For each sheet, verify: every present camper is paired AND every pair has the same swim color.</span>,
              "If something's wrong, message the counselor through Messages so it's documented.",
              "Trip days: confirm every camper has a trip lunch marked before the bus leaves.",
              "Tap PDF to download.",
            ]} />
            <div className="p-3 bg-amber-50 border-l-4 border-amber-500 rounded-r-lg">
              <p className="text-sm text-amber-900"><strong>Admin lock toggle:</strong> Admin can manually <strong>lock or unlock</strong> any group's Buddy Sheet from the Admin Dashboard. When locked, the counselor cannot edit pairs. If a counselor reports they're locked out after the 9:20 AM deadline, ask Admin to unlock — supervisors do not control the toggle.</p>
            </div>

            <Img caption="Buddy Sheets — grouped by date. Tap to expand, tap PDF to download." src={IMG.buddy} />
          </div>

          {/* ═══ 8. INCIDENT REPORTS ═══ */}
          <H id="incident-reports" num="8" title="Incident Reports — When Something Happens" icon={AlertTriangle} />
          <div className="mt-4 space-y-3">
            <p className="text-base text-gray-700">When a camper gets hurt, has a behavior issue, or something unusual happens, the counselor files an incident report. A badge on the Incident Reports card shows how many are open.</p>

            <Steps items={[
              "Tap the Incident Reports card. The badge tells you how many unread reports there are.",
              "Reports are grouped by date, newest first.",
              <span key="ir3">Each report shows: camper(s) involved, type (Medical / Behavior / Conflict / Other / Great Day), description, action already taken, and AI summary.</span>,
              "Decide follow-up: call parent, send to medical, behavioral plan, written warning, or nothing.",
              "Document your follow-up directly in the incident record (not by email or verbal).",
              "Tap PDF to download a printable copy.",
              "Target response: under 30 minutes during the camp day; overnight reports by 9:00 AM next morning.",
            ]} />

            <Callout title="Escalate to the camp director" color="rose">
              <ul className="list-disc pl-5 space-y-1">
                <li>Parent threatens legal action or media</li>
                <li>Anything you do not feel qualified to handle</li>
              </ul>
            </Callout>

            <Img caption="Incident Reports — badge shows open items. Read, respond, document." src={IMG.incidents} />
          </div>

          {/* ═══ 9. TRIP FORMS ═══ */}
          <H id="trip-forms" num="9" title="Trip Forms — Before Any Bus Leaves Camp" icon={MapPin} />
          <div className="mt-4 space-y-3">
            <p className="text-base text-gray-700">Before any group leaves camp on a trip, the counselor files a Trip Accountability Form. Your job is to spot-check these before the bus rolls.</p>

            <Steps items={[
              "Tap the Trip Forms card.",
              "Forms are grouped by date — tap a trip form to see the details.",
              <span key="tf4">Verify three things:<br/>
                <span className="block mt-1 text-sm">
                  1. Head count matches today's attendance.<br/>
                  2. Every camper is paired with a staff lead.<br/>
                  3. Medical flags accounted for and counselor has meds.
                </span>
              </span>,
              "If the form is incomplete 15 minutes before departure, find the counselor or message them. Do NOT let the bus leave without a complete form.",
              "After the group returns, confirm the return head count matches departure.",
              "Tap the PDF button to download a printable version.",
            ]} />
          </div>

          {/* ═══ 10. MESSAGES ═══ */}
          <H id="messages" num="10" title="Messages — How to Contact Parents" icon={MessageSquare} />
          <div className="mt-4 space-y-3">
            <p className="text-base text-gray-700">Text or email parents. SMS is the default. Each camper has its own conversation thread.</p>

            <Steps items={[
              "Tap the Messages card on your dashboard.",
              "Tap a group to open its inbox.",
              "Tap any camper card to see the full conversation.",
              "Type at the bottom, pick SMS or Email, tap Send.",
              "Use the date picker to find messages from a specific day.",
              "To message every family in the group, tap Message All.",
              "Parent replies come back into the same thread automatically.",
            ]} />

            <Callout title="Emergencies use the radio — not the app." color="indigo">
              <p>You can only message families in your division. Camp-wide messages are admin-only.</p>
            </Callout>

            <div className="p-3 bg-slate-50 border-l-4 border-slate-500 rounded-r-lg">
              <p className="text-sm text-slate-900"><strong>Off-season gate:</strong> All <strong>automated</strong> Twilio (SMS) and Resend (email) messages — daily dismissal reminders, attendance conflict pings, intramural standings emails, etc. — are <strong>blocked outside of camp dates (Jun 29 – Aug 20 ET)</strong>. Manual messages you send from this Messages screen still go through year-round.</p>
            </div>

            <Img caption="Messages — group inbox with per-camper conversation cards." src={IMG.messages} />
          </div>

          {/* ═══ 11. GROUP SCHEDULES ═══ */}
          <H id="group-schedule" num="11" title="Group Schedules — Regular / Special Event / Rainy Day" icon={CalendarDays} />
          <div className="mt-4 space-y-3">
            <p className="text-base text-gray-700">Every group in your division has a 10-minute master schedule that admin uploads. You can view (read-only) any group's schedule from your dashboard.</p>

            <Steps items={[
              "From your dashboard, tap the \"Schedules\" card.",
              "Pick a group. The schedule opens — Time down the left, Mon–Fri across the top.",
              "Use the three tabs at the top to switch between Regular, Special Event, and Rainy Day.",
            ]} />

            <Callout title="The three variants" color="sky">
              <div className="space-y-1 text-sm">
                <p><strong>Regular</strong> — the normal weekly schedule.</p>
                <p><strong>Special Event</strong> — Fridays, Color War, and the final week. Admin updates it as needed.</p>
                <p><strong>Rainy Day</strong> — indoor-only backup, used when the camp calls a rain day.</p>
              </div>
            </Callout>

            <p className="text-sm text-gray-700">Greyed-out tab = that variant hasn't been uploaded yet.</p>
          </div>

          {/* ═══ 12. HOBBIES & INTENSIVES ═══ */}
          <H id="hobbies-intensives" num="12" title="Hobbies & Intensives Reports" icon={BarChart3} />
          <div className="mt-4 space-y-3">
            <Callout title="Which divisions get what?" color="indigo">
              <ul className="list-disc pl-5 space-y-1">
                <li><strong>Mariners</strong> — Hobbies Reports card (Boys and Girls selections)</li>
                <li><strong>Navigators</strong> — Hobbies Reports card AND Intensives Reports card</li>
                <li><strong>Skippers</strong> — Neither card. Skippers do not have hobbies or intensives programming.</li>
              </ul>
            </Callout>

            <p className="text-base text-gray-700 font-bold mt-3">What are Hobbies?</p>
            <p className="text-sm text-gray-700">Each week, campers pick an activity (art, sports, drama, etc.). The system tracks who picked what. You monitor the selections and chase down any camper who hasn't picked.</p>

            <p className="text-base text-gray-700 font-bold mt-3">What are Intensives? (Navigators only)</p>
            <p className="text-sm text-gray-700">Intensives are a bigger commitment. If a Navigator picks Drama for Week 1, they are locked into Drama for all 6 weeks. This is different from Hobbies, which change weekly.</p>

            <p className="text-base text-gray-700 font-bold mt-3">How to use the reports:</p>
            <Steps items={[
              "From your dashboard, tap the Hobbies Reports or Intensives Reports card.",
              "Pick the week number. You see every activity running that week and how many campers signed up for each one.",
              "Look for any camper missing a selection. If someone hasn't picked, track down their counselor before the lock.",
              <span key="hi4"><strong>Selection windows:</strong> Mariner hobbies open <strong>Monday 12:00 AM</strong> and close <strong>Tuesday 2:00 PM</strong>. Navigator hobbies and Intensives open <strong>Monday 9:00 AM</strong> — hobbies close Thursday 2:00 PM, intensives close Tuesday 2:00 PM. After the lock, selections cannot be changed.</span>,
              "Need a physical printout? Ask Admin to generate the multi-tab XLSX or PDF.",
            ]} />

            <Mistakes items={[
              "Missing the Tuesday 2:00 PM lock for Mariners or the Thursday 2:00 PM lock for Navigators.",
              "Not reminding Navigator counselors that Week 1 Intensive selection locks the camper into that Intensive for all 6 weeks.",
            ]} />
          </div>

          {/* ═══ 12. MARINER INTRAMURALS ═══ */}
          <H id="mariner-intramurals" num="13" title="Mariner Intramurals (Mariners Supervisor ONLY)" icon={Trophy} />
          <div className="mt-4 space-y-3">
            <Callout title="This section is ONLY for the Mariners Supervisor" color="amber">
              <p>If you are Skippers or Navigators, skip this entire section. Mariner Intramurals does not involve you.</p>
            </Callout>

            <p className="text-base text-gray-700 font-bold mt-3">What are Mariner Intramurals?</p>
            <p className="text-sm text-gray-700">Mariner Intramurals is a sports competition within the Mariner division. Boys and Girls run separately. Campers from specific groups are sorted into teams, play a round-robin schedule, and finish with playoffs and final standings.</p>

            <div className="p-4 bg-blue-50 border-2 border-blue-300 rounded-xl my-4">
              <p className="font-bold text-sm text-blue-900 mb-2">Which groups participate:</p>
              <ul className="list-disc pl-5 space-y-1 text-sm text-blue-800">
                <li><strong>Boys Intramurals:</strong> B08, B09, B10, B11</li>
                <li><strong>Girls Intramurals:</strong> G08, G09, G10, G11</li>
              </ul>
            </div>

            <div className="p-4 bg-blue-50 border-2 border-blue-300 rounded-xl my-4">
              <p className="font-bold text-sm text-blue-900 mb-2">The teams (6 per gender, named after restaurants):</p>
              <div className="grid grid-cols-2 gap-4 mt-2">
                <div>
                  <p className="font-bold text-xs text-blue-800 mb-1">Boys Teams:</p>
                  <ol className="list-decimal pl-5 text-sm text-blue-800 space-y-0.5">
                    <li>Wendy's</li>
                    <li>Taco Bell</li>
                    <li>Subway</li>
                    <li>Panera</li>
                    <li>Chick-fil-A</li>
                    <li>Chipotle</li>
                  </ol>
                </div>
                <div>
                  <p className="font-bold text-xs text-blue-800 mb-1">Girls Teams:</p>
                  <ol className="list-decimal pl-5 text-sm text-blue-800 space-y-0.5">
                    <li>McDonald's</li>
                    <li>Pizza Hut</li>
                    <li>Burger King</li>
                    <li>KFC</li>
                    <li>Shake Shack</li>
                    <li>Domino's</li>
                  </ol>
                </div>
              </div>
            </div>

            <p className="text-base text-gray-700 font-bold mt-3">How it works:</p>
            <Steps items={[
              <span key="mi1"><strong>Team sorting:</strong> Before the season starts, the Athletics Supervisor (or Admin) auto-sorts campers from the participating groups into the 6 teams. This is done in the Athletics Dashboard or Admin Dashboard — not yours. You do not sort the teams yourself.</span>,
              <span key="mi2"><strong>Schedule & Scores:</strong> The Athletics Supervisor enters game scores after each match day. The system uses the same scoring as Nav Intramurals: <strong>Win = 10 points, Tie = 5 points, Loss = 3 points</strong>. Standings are auto-calculated from the real game scores.</span>,
              <span key="mi3"><strong>Playoffs:</strong> After the round-robin, top teams advance to playoffs. The Athletics Supervisor manages the playoff bracket.</span>,
              <span key="mi4"><strong>Final Standings:</strong> The system generates final standings combining regular season and playoff results.</span>,
              <span key="mi5"><strong>PDF Export:</strong> A full PDF of teams, schedule, and standings can be generated for printing.</span>,
            ]} />

            <p className="text-base text-gray-700 font-bold mt-3">Your role as Mariners Supervisor:</p>
            <Steps items={[
              "The Athletics Supervisor (or Admin) sorts teams, enters scores, and manages the bracket.",
              "Counselors in B08–B11 / G08–G11 see team assignments, schedule, and standings read-only.",
              "Your job is oversight: scores are entered on game days, teams are balanced, counselors know the schedule.",
              "Camper needs to move teams (injury, group change)? Ask the Athletics Supervisor or Admin.",
              "Score wrong or missing? Flag it to the Athletics Supervisor.",
            ]} />
          </div>

          {/* ═══ 13. NAV INTRAMURALS ═══ */}
          <H id="nav-intramurals" num="14" title="Navigator Intramurals (Navigators Supervisor ONLY)" icon={Trophy} />
          <div className="mt-4 space-y-3">
            <Callout title="Navigators Supervisor only" color="amber">
              <p>Skip this section if you are Mariners or Skippers.</p>
            </Callout>

            <p className="text-sm text-gray-700">Co-ed sports competition for all Navigator groups (B12–B15, G12–G15). 10 teams, 6-week round robin, standings, and playoffs. <strong>Unlike Mariner Intramurals, you enter the scores.</strong></p>

            <div className="p-4 bg-teal-50 border-2 border-teal-300 rounded-xl my-4">
              <p className="font-bold text-sm text-teal-900 mb-2">The 10 Navigator Teams:</p>
              <div className="grid grid-cols-2 gap-x-6 gap-y-0.5 mt-1">
                {[
                  "Chaos Crew", "The Dreamers", "Focus Force", "The Fearless", "Mind Masters",
                  "The Hyped", "The Chill Zone", "The Overthinkers", "The Positive Vibes", "The Dark Side"
                ].map((t, i) => <p key={i} className="text-sm text-teal-800">{i+1}. {t}</p>)}
              </div>
            </div>

            <div className="p-4 bg-teal-50 border-2 border-teal-300 rounded-xl my-4">
              <p className="font-bold text-sm text-teal-900 mb-2">Activities & Locations:</p>
              <ul className="list-disc pl-5 text-sm text-teal-800 space-y-0.5">
                <li>Hockey — Hockey Rink</li>
                <li>Softball — Softball 1</li>
                <li>Spot Shot — Basketball Court</li>
                <li>Euro Handball — Field 2</li>
                <li>Volleyball — Volleyball Court</li>
              </ul>
            </div>

            <div className="p-4 bg-amber-50 border-2 border-amber-400 rounded-xl my-4">
              <p className="font-bold text-amber-900 mb-2">Scoring</p>
              <p className="text-sm text-amber-800">Enter the actual game score (e.g. 12–6). System auto-converts: <strong>Win = 10 pts, Tie = 5 pts, Loss = 3 pts</strong>.</p>
            </div>

            <p className="text-base text-gray-700 font-bold mt-3">How to enter scores:</p>
            <Steps items={[
              "Tap the Nav Intramurals card → Schedule/Scores.",
              "Find today's match. Type the actual score in each team's box (e.g. 12 and 6).",
              "Tap Save. Standings and rankings update instantly.",
              "Wrong score? Change the numbers and save again.",
            ]} />

            <p className="text-base text-gray-700 font-bold mt-3">Other tabs:</p>
            <ul className="list-disc pl-6 space-y-1 text-sm text-gray-700">
              <li><strong>Teams:</strong> See team rosters; sort or move campers between teams.</li>
              <li><strong>Standings:</strong> Full table — wins, losses, ties, points.</li>
              <li><strong>Rankings:</strong> Teams ranked with trend arrows.</li>
              <li><strong>Playoffs:</strong> Set up and manage the playoff bracket after the round-robin.</li>
            </ul>
          </div>

          {/* ═══ 14. ATTENDANCE PORTAL ═══ */}
          <H id="attendance-portal" num="15" title="The Attendance Admin Portal (External Website)" icon={ExternalLink} />
          <div className="mt-4 space-y-3">
            <p className="text-base text-gray-700">The Attendance system lives on a <strong>separate website</strong> — <strong>rrdcattendance.com</strong>. The Attendance card on your dashboard is a link that opens this website in a new tab. It is NOT inside Camper Snapshot.</p>

            <p className="text-base text-gray-700 font-bold mt-3">When to use it:</p>
            <ul className="list-disc pl-6 space-y-1 text-sm text-gray-700">
              <li>To correct a mistaken attendance entry</li>
              <li>To add a manual attendance mark for a camper who was missed</li>
              <li>To override a bus check-in</li>
              <li>To view historical attendance records</li>
            </ul>

            <p className="text-base text-gray-700 font-bold mt-3">How to use it:</p>
            <Steps items={[
              "On your dashboard, tap the Attendance card (it has an external link icon in the corner).",
              "A new browser tab opens to rrdcattendance.com.",
              "Log in with the same username and password you use for Camper Snapshot supervisor login.",
              "Make your changes — add a mark, correct an entry, override a record.",
              "Close the tab when done. Changes sync back into Camper Snapshot within about a minute.",
            ]} />

            <Why>Camper Snapshot is your dashboard. rrdcattendance.com is the system of record. When they disagree about attendance, the admin portal wins. That is where you fix data.</Why>
          </div>

          {/* ═══ 15. DINING PORTAL ═══ */}
          <H id="dining-portal" num="16" title="The Dining Admin Portal — Kitchen View (External Website)" icon={Utensils} />
          <div className="mt-4 space-y-3">
            <p className="text-base text-gray-700">The Dining admin portal (<strong>rrdcdining.com → Kitchen / Admin Access</strong>) is the kitchen's view of all lunch orders. It is a kitchen tool — but you need to know how it works to add late orders and verify your division's lunches.</p>

            <p className="text-base text-gray-700 font-bold mt-3">Login</p>
            <Steps items={[
              <span key="d1">Go to <strong>rrdcdining.com</strong> → <strong>Kitchen / Admin Access</strong> at the bottom.</span>,
              <span key="d2">Password: <strong>Camp1993!</strong></span>,
            ]} />

            <p className="text-base text-gray-700 font-bold mt-4">Camp runs 5 lunch periods</p>
            <p className="text-sm text-gray-600">The kitchen cooks for one period at a time. The portal organizes everything by period.</p>

            <p className="text-base text-gray-700 font-bold mt-4">Three views:</p>
            <div className="space-y-3">
              <div className="p-4 bg-amber-50 border-2 border-amber-300 rounded-xl">
                <p className="font-bold text-amber-900 mb-2">1. Period Totals</p>
                <p className="text-sm text-amber-800">Total of each order type across all groups in that period — what the kitchen cooks.</p>
              </div>
              <div className="p-4 bg-blue-50 border-2 border-blue-300 rounded-xl">
                <p className="font-bold text-blue-900 mb-2">2. Group Breakdown per Period</p>
                <p className="text-sm text-blue-800">Drill into a group to see how many of each order type that group ordered.</p>
              </div>
              <div className="p-4 bg-green-50 border-2 border-green-300 rounded-xl">
                <p className="font-bold text-green-900 mb-2">3. Individual Camper Orders</p>
                <p className="text-sm text-green-800">Every camper line by line with order and allergy flag (allergies highlighted in red).</p>
              </div>
            </div>

            <p className="text-base text-gray-700 font-bold mt-4">How lunch service works:</p>
            <Steps items={[
              <span key="k1"><strong>Before 9:30 AM:</strong> Counselors order lunches at rrdcdining.com.</span>,
              <span key="k2"><strong>At 9:30 AM:</strong> Counselor window closes. Kitchen pulls totals and starts cooking.</span>,
              <span key="k3"><strong>During service:</strong> Each group eats during its assigned period.</span>,
              <span key="k4"><strong>Late orders:</strong> Add it in the portal, then walk to the kitchen and tell them — they don't sit refreshing the screen.</span>,
            ]} />
          </div>


          {/* ═══ 16. AQUATICS ═══ */}
          <H id="aquatics" num="17" title="Aquatics — Swim Track (External Website)" icon={Waves} />
          <div className="mt-4 space-y-3">
            <p className="text-base text-gray-700"><strong>rrdcaquatics.com</strong> tracks every camper's swim color, swim level, and program. The Aquatics Supervisor uses it for assessments, reports, and parent communication.</p>

            <div className="p-4 bg-red-50 border-2 border-red-500 rounded-xl" data-testid="aquatics-non-negotiable">
              <p className="font-bold text-sm text-red-900 mb-1">Non-Negotiable: Buddy Sheet check at the pool</p>
              <p className="text-sm text-red-800">
                Before any group enters the water, open and check that group's Buddy Sheet on the pool deck. Re-check at every buddy whistle.
              </p>
              <ul className="list-disc pl-5 mt-2 space-y-1 text-sm text-red-800">
                <li>Every camper at the pool is on the sheet, and every camper on the sheet is at the pool.</li>
                <li>Pairs are matched by swim color (Green / Yellow / Red).</li>
                <li>At every whistle both campers in a pair raise hands. If one doesn't, clear the pool.</li>
                <li>Anything wrong — stop the swim until it's fixed.</li>
              </ul>
            </div>

            <div className="p-4 bg-cyan-50 border-2 border-cyan-300 rounded-xl">
              <p className="font-bold text-sm text-cyan-900 mb-2">Aquatics Login</p>
              <p className="text-sm text-cyan-800"><strong>rrdcaquatics.com</strong> → passcode <strong>Camp1993</strong>.</p>
            </div>

            <p className="text-base text-gray-700 font-bold mt-4">Swim Track Dashboard</p>

            <div className="p-4 bg-cyan-50 border-2 border-cyan-300 rounded-xl">
              <p className="font-bold text-cyan-900 mb-2">Camp-Wide Summary (top)</p>
              <div className="flex flex-wrap gap-2 mt-2">
                <span className="px-2 py-1 rounded-full text-xs font-bold bg-green-100 text-green-800 border border-green-300">G = Green (deep end)</span>
                <span className="px-2 py-1 rounded-full text-xs font-bold bg-yellow-100 text-yellow-800 border border-yellow-300">Y = Yellow (mid depth)</span>
                <span className="px-2 py-1 rounded-full text-xs font-bold bg-red-100 text-red-800 border border-red-300">R = Red (shallow only)</span>
                <span className="px-2 py-1 rounded-full text-xs font-bold bg-pink-100 text-pink-800 border border-pink-300">R/B</span>
                <span className="px-2 py-1 rounded-full text-xs font-bold bg-lime-100 text-lime-800 border border-lime-300">Y/B</span>
                <span className="px-2 py-1 rounded-full text-xs font-bold bg-teal-100 text-teal-800 border border-teal-300">G/B</span>
              </div>
              <p className="text-xs text-cyan-700 mt-2"><strong>/B</strong> = passed the dock test (cleared for boating and river activities).</p>
            </div>

            <div className="p-4 bg-blue-50 border-2 border-blue-300 rounded-xl">
              <p className="font-bold text-blue-900 mb-2">4 Division Cards</p>
              <p className="text-sm text-blue-800">Skippers, Mariners, Navigators, CIT — each with live camper count and swim-color breakdown. Tap to drill in.</p>
            </div>

            <p className="text-base text-gray-700 font-bold mt-4">Drill-down: Division → Group → Camper</p>
            <Steps items={[
              "Tap a division to see its groups with camper counts and color breakdown.",
              "Tap a group to see every camper with name, swim level (1–4), program, and color badge.",
              "Search by name; filter by level or color.",
              "Tap a camper to update their swim color or level after a test.",
            ]} />

            <p className="text-base text-gray-700 font-bold mt-4">Top Navigation Buttons:</p>
            <div className="space-y-2">
              <div className="p-3 bg-gray-50 border rounded-lg flex items-start gap-3">
                <span className="px-2 py-0.5 rounded-full text-xs font-bold bg-white border">Quick</span>
                <p className="text-xs text-gray-700"><strong>Quick Sync</strong> — pulls new campers and updated swim data from CampMinder. Use daily.</p>
              </div>
              <div className="p-3 bg-gray-50 border rounded-lg flex items-start gap-3">
                <span className="px-2 py-0.5 rounded-full text-xs font-bold bg-white border">Full</span>
                <p className="text-xs text-gray-700"><strong>Full Sync</strong> — complete re-sync. Slower. Use weekly or after a sync miss.</p>
              </div>
              <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg flex items-start gap-3">
                <span className="px-2 py-0.5 rounded-full text-xs font-bold bg-blue-600 text-white">Daily</span>
                <p className="text-xs text-blue-800"><strong>Daily View</strong> — today's swim schedule, pool, and lifeguard assignments.</p>
              </div>
              <div className="p-3 bg-green-50 border border-green-200 rounded-lg flex items-start gap-3">
                <span className="px-2 py-0.5 rounded-full text-xs font-bold bg-green-600 text-white">Report</span>
                <p className="text-xs text-green-800"><strong>Swim Report</strong> — printable report by division or group. Use for lifeguard briefings.</p>
              </div>
              <div className="p-3 bg-indigo-50 border border-indigo-200 rounded-lg flex items-start gap-3">
                <span className="px-2 py-0.5 rounded-full text-xs font-bold bg-indigo-600 text-white">Email</span>
                <p className="text-xs text-indigo-800"><strong>Email Parents</strong> — sends swim color notification emails to parents. Use this after swim assessments to let families know their child's updated pool level.</p>
              </div>
              <div className="p-3 bg-pink-50 border border-pink-200 rounded-lg flex items-start gap-3">
                <span className="px-2 py-0.5 rounded-full text-xs font-bold bg-pink-600 text-white">Flyer</span>
                <p className="text-xs text-pink-800"><strong>Swim Color Flyer</strong> — generates a printable flyer explaining what each swim color means (pool depth, rules). Post these at the pool entrance.</p>
              </div>
            </div>

            <p className="text-base text-gray-700 font-bold mt-4">Swim Colors — What They Mean:</p>
            <div className="space-y-2">
              <div className="p-3 bg-green-50 border border-green-200 rounded-lg"><strong className="text-green-800">Green</strong> — <span className="text-sm text-green-700">Deep end. Camper passed the full swim test. Can swim anywhere in the pool.</span></div>
              <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg"><strong className="text-yellow-800">Yellow</strong> — <span className="text-sm text-yellow-700">Mid depth. Camper can swim but not strong enough for the deep end. Stays in the middle section.</span></div>
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg"><strong className="text-red-800">Red</strong> — <span className="text-sm text-red-700">Shallow end only. Camper is a beginner or non-swimmer. Must stay where they can touch the bottom.</span></div>
            </div>
            <p className="text-sm text-gray-600 mt-2">R/B = Red + dock test passed. Y/B = Yellow + dock test passed. G/B = Green + dock test passed. Blue means the camper is cleared for boating and river activities in addition to their pool level.</p>

            <Why>Swim colors are a life-safety classification. The buddy sheet pairing system (Section 7) requires that buddies have the SAME swim color. If a Red camper is paired with a Green camper, one of them ends up in water they can't handle. The Aquatics app is the source of truth for every camper's pool access level.</Why>

            <Mistakes items={[
              "Overriding a swim color because a parent says their kid 'can swim.' Only the Aquatics Supervisor changes swim colors after a real test.",
              "Not syncing before swim assessments. If new campers enrolled this week, they won't appear until you sync.",
              "Forgetting to email parents after re-assessments. Parents need to know if their child's swim color changed.",
            ]} />
          </div>


          {/* ═══ 17. CIT PROGRAM ═══ */}
          <H id="cit-program" num="18" title="CIT Program — Counselors in Training" icon={Users} />
          <div className="mt-4 space-y-3">
            <p className="text-base text-gray-700">CITs (Counselors in Training) are <strong>campers</strong>, but they are NOT like the younger campers. They are older teens being trained to become counselors. They have <strong>2 CIT Supervisors</strong> who guide them, manage their schedules, and handle their incident reports. They do NOT participate in younger-camper activities.</p>

            <div className="p-4 bg-red-50 border-2 border-red-500 rounded-xl">
              <p className="font-bold text-red-900 mb-2">What CITs do NOT have:</p>
              <ul className="list-disc pl-5 space-y-1 text-sm text-red-800">
                <li><strong>No buddy sheets</strong> — CITs do not go to the pool with the younger campers. They do not get paired into swim buddies.</li>
                <li><strong>No lunch orders</strong> — CITs do not order through the dining system. They eat separately.</li>
                <li><strong>No hobbies / intensives</strong> — CITs have their own training schedule, not the elective program.</li>
              </ul>
            </div>

            <div className="p-4 bg-violet-50 border-2 border-violet-300 rounded-xl">
              <p className="font-bold text-sm text-violet-900 mb-2">CIT Login Credentials</p>
              <p className="text-sm text-violet-800">The CIT group logs in on the <strong>Counselor tab</strong> (not the Supervisor tab):</p>
              <ul className="list-disc pl-5 mt-1 space-y-0.5 text-sm text-violet-800">
                <li><strong>Group Code:</strong> <span className="font-mono bg-violet-100 px-1 rounded">cit</span></li>
                <li><strong>Password:</strong> <span className="font-mono bg-violet-100 px-1 rounded">cit</span></li>
              </ul>
            </div>

            <p className="text-base text-gray-700 font-bold mt-4">What the CIT dashboard has:</p>
            <p className="text-sm text-gray-600 mb-2">The CIT dashboard is stripped down compared to a regular counselor group. It only shows what's relevant to CITs:</p>

            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-sm">
              {[
                "Campers", "Daily Roster", "Incident Report",
                "Messages", "Alerts", "Conflict Match", "Group Attendance"
              ].map(c => (
                <div key={c} className="p-2 bg-gray-50 border rounded-lg text-center text-gray-700">{c}</div>
              ))}
            </div>
            <p className="text-xs text-gray-500 mt-1">Standard cards that work the same as any group — attendance, roster, messages, alerts, incident reports.</p>

            <p className="text-base text-gray-700 font-bold mt-4">CIT-Only features (these do NOT appear on any other group's dashboard):</p>

            <div className="p-4 bg-orange-50 border-2 border-orange-300 rounded-xl">
              <p className="font-bold text-orange-900 mb-2">CIT Trips — Flex Day Enrollment with Color Badges</p>
              <p className="text-sm text-orange-800">CITs go on <strong>8 trips</strong> throughout the summer. Unlike younger campers who all go on the same trip, each CIT individually confirms whether they are going on each trip. This creates a "flex day" system — not every CIT goes on every trip.</p>
              <p className="text-sm text-orange-800 mt-2">The screen shows every CIT camper listed alphabetically. Each CIT has a row with 8 colored badges — one for each trip:</p>
              <div className="mt-3 space-y-2">
                <div className="flex items-center gap-3">
                  <span className="inline-block px-3 py-1 rounded-full text-xs font-bold bg-green-100 text-green-800 border border-green-300">Trip 3: Yes</span>
                  <span className="text-sm text-orange-800"><strong>Green "Yes"</strong> — This CIT IS going on this trip. Confirmed by parent.</span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="inline-block px-3 py-1 rounded-full text-xs font-bold bg-red-100 text-red-800 border border-red-300">Trip 3: No</span>
                  <span className="text-sm text-orange-800"><strong>Red "No"</strong> — This CIT is NOT going on this trip. Parent said no.</span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="inline-block px-3 py-1 rounded-full text-xs font-bold bg-gray-100 text-gray-500 border border-gray-300">Trip 3: —</span>
                  <span className="text-sm text-orange-800"><strong>Gray dash "—"</strong> — Unconfirmed. Parent has not responded yet. This is the problem one.</span>
                </div>
              </div>
              <p className="text-sm text-orange-800 mt-3"><strong>Sync button</strong> (top right corner) — Tap this to pull the latest trip enrollment data from CampMinder. Do this whenever you know parent confirmations have come in.</p>
              <p className="text-sm text-orange-800 mt-2"><strong>Before every trip:</strong> Open this screen and scan for gray badges. Every CIT should be either green (Yes) or red (No). If anyone is still gray (—) the day before the trip, the CIT supervisor needs to call that family and get a yes or no answer. You cannot put a CIT on a bus if their parent hasn't confirmed.</p>
            </div>

            <div className="p-4 bg-blue-50 border-2 border-blue-300 rounded-xl mt-3">
              <p className="font-bold text-blue-900 mb-2">Paperwork Submitted — CIT Parent Signature</p>
              <p className="text-sm text-blue-800">Before a CIT can start the program, their parent must submit a signed consent form. This screen tracks who has submitted and who hasn't.</p>
              <p className="text-sm text-blue-800 mt-2">Shows a counter at the top — submitted count vs total CIT count.</p>
              <p className="text-sm text-blue-800 mt-1">Below the counter, each CIT who HAS submitted is listed with:</p>
              <ul className="list-disc pl-5 mt-1 space-y-0.5 text-sm text-blue-800">
                <li><strong>CIT name</strong></li>
                <li><strong>Parent name</strong> — which parent signed</li>
                <li><strong>Date submitted</strong> — when it came in</li>
              </ul>
              <p className="text-sm text-blue-800 mt-2"><strong>Sync button</strong> pulls the latest data from CampMinder.</p>
              <p className="text-sm text-blue-800 mt-2"><strong>Before the program starts</strong>, the submitted count must equal the total CIT count. If anyone is missing, the CIT supervisors chase down those families. No signed form = that CIT cannot participate.</p>
            </div>

            <div className="p-4 bg-rose-50 border-2 border-rose-300 rounded-xl mt-3">
              <p className="font-bold text-rose-900 mb-2">Incident Reports — CIT Supervisor handles</p>
              <p className="text-sm text-rose-800">CIT supervisors file and manage incident reports for CITs. Division supervisors don't get involved unless it escalates — same escalation path as any other incident.</p>
            </div>

            <div className="p-4 bg-purple-50 border-2 border-purple-300 rounded-xl mt-3">
              <p className="font-bold text-purple-900 mb-2">Private Camper Notes — CIT Supervisors + Admin only</p>
              <p className="text-sm text-purple-800">Every CIT's Camper Card has a <strong>Private Notes</strong> section visible <strong>only</strong> to the 2 CIT Supervisors and Admin. Counselors, other division supervisors, and the CITs themselves cannot see it. Use it for sensitive evaluation notes, readiness flags, or family context that should not be visible to anyone else.</p>
              <ul className="list-disc pl-5 mt-2 space-y-0.5 text-sm text-purple-800">
                <li>Open the CIT's Camper Card → scroll to <strong>Private Notes</strong>.</li>
                <li>Type the note, tap Save. Timestamped automatically.</li>
                <li>Notes persist across the season. Reviewed at end-of-summer for counselor hiring decisions.</li>
              </ul>
            </div>

            <p className="text-base text-gray-700 font-bold mt-4">Key facts:</p>
            <ul className="list-disc pl-6 space-y-2 text-sm text-gray-700">
              <li><strong>One CIT group</strong> — all CITs are in a single group, the largest single group at camp.</li>
              <li><strong>8 trips</strong> over the summer — each trip is opt-in, confirmed individually by parents.</li>
              <li><strong>2 CIT Supervisors</strong> — they manage CITs day-to-day.</li>
              <li><strong>Training focus</strong> — CITs shadow counselors and assist with activities; they're evaluated on readiness to become counselors.</li>
              <li><strong>Attendance applies</strong> — CITs are marked present/absent daily through Group Attendance.</li>
            </ul>
          </div>

          {/* ═══ 18. ATHLETICS DASHBOARD ═══ */}
          <H id="athletics-dashboard" num="19" title="Athletics Supervisor Dashboard" icon={Trophy} />
          <div className="mt-4 space-y-3">
            <p className="text-base text-gray-700">The Athletics Supervisor manages Mariner Intramurals — the older-Mariner (3rd & 4th grade, groups B08–B11 and G08–G11) sports competition. Login on the Camper Snapshot login page → <strong>Athletics</strong> tab → password <strong>Camp1993!</strong>.</p>

            <div className="p-4 bg-red-50 border-2 border-red-500 rounded-xl" data-testid="athletics-non-negotiable">
              <p className="font-bold text-sm text-red-900 mb-1">Non-Negotiable: Daily score entry</p>
              <p className="text-sm text-red-800">
                You are the only person who enters Mariner Intramural game scores (boys and girls). Scores must be entered the same day a match is played — same flow the Navigators Supervisor uses. Standings (Win=10, Tie=5, Loss=3), playoff seeding, and final rankings depend on day-of entry.
              </p>
            </div>

            <p className="text-base text-gray-700 font-bold mt-4">The 7 cards on the Athletics dashboard:</p>

            <div className="space-y-2">
              <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="font-bold text-sm text-blue-900">1. Boys Intramurals & 2. Girls Intramurals</p>
                <p className="text-xs text-blue-800">Sort campers into 6 teams. Auto-sort or drag-and-drop between teams.</p>
              </div>
              <div className="p-3 bg-emerald-50 border-2 border-emerald-400 rounded-lg">
                <p className="font-bold text-sm text-emerald-900">3. Boys Schedule & Scorecard & 4. Girls Schedule & Scorecard <span className="text-[10px] bg-red-600 text-white px-1.5 py-0.5 rounded ml-1">DAILY DUTY</span></p>
                <p className="text-xs text-emerald-800">Open the day's matchups, type the score for each team, save. Standings auto-update.</p>
              </div>
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <p className="font-bold text-sm text-amber-900">5. Playoffs</p>
                <p className="text-xs text-amber-800">Manage the bracket after the round-robin. Enter playoff scores, advance winners.</p>
              </div>
              <div className="p-3 bg-purple-50 border border-purple-200 rounded-lg">
                <p className="font-bold text-sm text-purple-900">6. Final Standings</p>
                <p className="text-xs text-purple-800">Official end-of-season rankings — 1st through 6th, boys and girls.</p>
              </div>
              <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg">
                <p className="font-bold text-sm text-gray-900">7. Athletics Shed — Equipment Sign-Out Log</p>
                <p className="text-xs text-gray-700">Tracks who borrowed equipment, when, and whether it was returned.</p>
              </div>
            </div>

            <Why>Mariners Supervisor sees standings read-only; only you can enter scores.</Why>
          </div>

          {/* ═══ 19. COLOR WAR ═══ */}
          <H id="color-war" num="20" title="Color War — The Reveal Switch" icon={Flag} />
          <div className="mt-4 space-y-3">
            <p className="text-base text-gray-700">Camp splits into Blue vs White. Team assignments are pre-generated and hidden until the official reveal.</p>

            <Steps items={[
              "Tap the Color War card.",
              "You see every group in your division with Blue/White counts.",
              "Tap a group to see who is on which team. View-only.",
              "Admin controls the reveal toggle — it shows or hides team badges across the app.",
            ]} />

            <p className="text-base text-gray-700 font-bold mt-3">Rules:</p>
            <ul className="list-disc pl-6 space-y-1 text-sm text-gray-700">
              <li><strong>Sibling rule:</strong> Same last name = opposite teams.</li>
              <li><strong>Skippers exception:</strong> In Skippers (Nursery–K), siblings stay on the same team.</li>
              <li><strong>Do NOT reveal teams early.</strong> Wait for the director's signal.</li>
            </ul>
          </div>

          {/* ═══ 20. TESTING TOOLS ═══ */}
          <H id="testing-tools" num="21" title="Testing Tools — DO NOT TOUCH During Summer" icon={Trash2} />
          <div className="mt-4 space-y-3">
            <Rule>Destructive. Password-protected. Pre-season only. There is no undo.</Rule>

            <Steps items={[
              "Tap the Testing Tools card.",
              "Enter the Clear Data password (get it from the camp director).",
              "You see a grid of data categories. Tap \"Clear All\" next to any to wipe that category.",
              "Confirm. Data is gone forever.",
            ]} />

            <Callout title="When to use" color="rose">
              <p>Once, right before opening day. Never during the season.</p>
            </Callout>
          </div>

          {/* ═══ 21. ESCALATION PATH ═══ */}
          <H id="escalation" num="22" title="When Things Go Wrong — Who to Call" icon={HelpCircle} />
          <div className="mt-4 space-y-3">
            <div className="border rounded-lg p-4 bg-green-50 my-3">
              <p className="font-bold text-sm text-green-900 mb-2">FIX IT YOURSELF</p>
              <ul className="list-disc pl-5 text-sm text-green-800 space-y-1">
                <li>Red attendance conflict — Accept App or Accept Bus</li>
                <li>Missing lunch for a late arrival — add it in the Dining admin portal</li>
                <li>Buddy sheet swim-color mismatch — message the counselor</li>
                <li>Incident report follow-up — document in the incident record</li>
                <li>Incomplete trip form — find the counselor / message them</li>
                <li>Counselor hasn't submitted attendance by 10:00 AM — track them down</li>
                <li>Mariner intramural score missing on game day — flag to Athletics Supervisor</li>
                <li>Navigator intramural score missing — enter it yourself</li>
              </ul>
            </div>

            <div className="border rounded-lg p-4 bg-amber-50 my-3">
              <p className="font-bold text-sm text-amber-900 mb-2">CALL BRIAN STEIN (technical)</p>
              <ul className="list-disc pl-5 text-sm text-amber-800 space-y-1">
                <li>App down or not loading for multiple groups</li>
                <li>SMS or email not delivering</li>
                <li>Persistent CampMinder sync errors</li>
                <li>Bug in a feature</li>
                <li>Corrupt PDF/XLSX export</li>
              </ul>
            </div>

            <div className="border rounded-lg p-4 bg-red-50 my-3">
              <p className="font-bold text-sm text-red-900 mb-2">ESCALATE TO THE CAMP DIRECTOR</p>
              <ul className="list-disc pl-5 text-sm text-red-800 space-y-1">
                <li>Parent threatens legal action or media</li>
                <li>Anything you don't feel qualified to resolve</li>
              </ul>
            </div>
          </div>

          {/* Footer */}
          <footer className="mt-12 pt-6 border-t text-center space-y-3 pb-8">
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 inline-block">
              <p className="text-sm font-bold text-emerald-900">Tech Support: Brian Stein</p>
            </div>
            <p className="text-xs text-gray-400">Rolling River Day Camp — Supervisor Tutorial — Last updated February 2026</p>
          </footer>
        </main>
      </div>

      {showBackToTop && (
        <button onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} className="fixed bottom-6 right-6 bg-emerald-700 text-white p-3 rounded-full shadow-lg hover:bg-emerald-600 z-50 print:hidden" data-testid="back-to-top-button"><ChevronUp className="w-5 h-5" /></button>
      )}
    </div>
  );
}
